import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  CircularProgress,
} from "@mui/material";
import { H1, H3, Small } from "../../components/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { spalshItems } from "../../constants/constant";
import { maxPhoneLength, partnerCode as defaultPartnerCode } from "../../common/common";
import Carousel from "react-material-ui-carousel";
import { SignInAcruRequest } from "../../models";
import { userLogin } from "../../redux/actions/LoginAction";
import { useAppDispatch } from "../../store/hooks";

export default function Login() {
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assistedMode = searchParams.get("mode") === "Assisted";
  const partnerCode = searchParams.get("id") || defaultPartnerCode;
  const assistedMobile = searchParams.get("mobile");
  const [phoneNumber, setPhoneNumber] = useState(
    assistedMode && assistedMobile ? assistedMobile : ""
  );
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    const preloadImages = () => {
      const imagePromises = spalshItems.map((item) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = item.image;
          img.onload = resolve;
          img.onerror = reject;
        });
      });

      Promise.all(imagePromises)
        .then(() => {
          setIsLoaded(true);
        })
        .catch((error) => {
          setIsLoaded(true); // Set isLoaded to true even if there was an error to prevent blocking the UI
        });
    };

    preloadImages();
  }, []);

  const handleSubmit = () => {
    const regex = /^[0-9\b]+$/;
    const number = /^(?:(?:\+|0{0,2})91(\s*[\-]\s*)?|[0]?)?[6789]\d{9}$/;
    if (phoneNumber === "") {
      setShowError(true);
      setErrorMessage("Please enter a mobile number.");
      return;
    } else if (!regex?.test(phoneNumber)) {
      setShowError(true);
      setErrorMessage("Please enter a valid mobile number.");
      return;
    } else if (!number?.test(phoneNumber)) {
      setShowError(true);
      setErrorMessage("Please enter valid indian mobile number");
    } else if (phoneNumber?.length < 10) {
      setShowError(true);
      setErrorMessage("Please enter a valid mobile number.");
      return;
    } else {
      const data: SignInAcruRequest = {
        mobile: phoneNumber,
        partner: partnerCode,
        terms_accepted: true,
        hashCode: " ",
      };

      dispatch(userLogin(data))
        .then(async (response: any) => {
          if (response.statusCode === 200) {
            navigate("/otp", {
              state: {
                number: phoneNumber,
                partnerCode,
              },
            });
            toast.success(response.data.message);
          }
        })
        .catch((error: any) => {
          toast.error(error);
        });
    }
  };

  return (
    <Grid
      container
      component="main"
      sx={{
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        flex: 1,
      }}
    >
      <CssBaseline />
      {/* Left Side  */}
      <Grid item xs={false} sm={4} md={6} sx={sxStyleGrid}>
        {partnerCode === "0" ? (
          <Box sx={sxStyleBoxCaresoul}>
            {isLoaded ? (
              <Carousel animation="slide">
                {spalshItems?.map((item) => (
                  <Box key={item.id}>
                    <Grid sx={sxStyleGridCaresoul}>
                      <Box
                        component="img"
                        src={item.image}
                        sx={sxStyleImageCaresoul}
                      />
                    </Grid>
                  </Box>
                ))}
              </Carousel>
            ) : (
              <Grid
                height="70vh"
                flexDirection="column"
                sx={{
                  display: { xs: "none", md: "flex", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <CircularProgress />
              </Grid>
            )}
          </Box>
        ) : (
          <Box sx={sxStyleBoxCaresoulPartner}>
            {isLoaded ? (
              <Carousel animation="slide">
                {spalshItems?.map((item) => (
                  <Box key={item.id}>
                    <Grid sx={sxStyleGridCaresoul}>
                      <Box
                        component="img"
                        src={item.image}
                        sx={sxStyleImageCaresoul}
                      />
                    </Grid>
                  </Box>
                ))}
              </Carousel>
            ) : (
              <Grid
                height="70vh"
                flexDirection="column"
                sx={{
                  display: { xs: "none", md: "flex", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <CircularProgress />
              </Grid>
            )}
            <Box
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                sx={{
                  height: {
                    xs: "10%",
                    sm: "10%",
                    md: "25%",
                  },
                  width: {
                    xs: "17%",
                    sm: "17%",
                    md: "35%",
                  },
                  mt: 5,
                }}
                src={process.env.REACT_APP_PARTNERSHIP}
                alt="saas-dasboard-footer"
              />
            </Box>
          </Box>
        )}
      </Grid>

      {/* Right Side */}
      <Grid item xs={12} sm={4} md={6} component={Paper} sx={sxStyleGrid1}>
        <Grid md={10} sx={sxStyleBox}>
          <Grid
            sx={{
              justifyContent: {
                md: "flex-start",
                sm: "flex-start",
                xs: "center",
              },
              alignItems: "center",
              display: "flex",
            }}
          >
            <Box
              component="img"
              src={process.env.REACT_APP_LOGO}
              sx={sxStyleImage}
            />
          </Grid>

          <Box
            flexDirection="column"
            display="flex"
            component="img"
            src="https://acru-asset.s3.us-east-1.amazonaws.com/assets/newLoginImage.svg"
            sx={sxStyleImage1}
          />
          <H1
            fontSize={24}
            fontWeight={600}
            sx={sxStyleH1}
            color={process.env.REACT_APP_PRIMARY_COLOR}
          >
            Sign in to continue
          </H1>
          <Grid sx={sxStyleGrid2}>
            <Small fontWeight={400} sx={sxStyleSmall} color="#595959">
              Enter valid mobile number,<br></br>
              <Small fontWeight={400} sx={sxStyleSmall} color="#595959">
                we will send you an OTP for verification
              </Small>
            </Small>
          </Grid>

          <Box sx={sxStyleBox1}>
            <Box sx={sxStyleBox2}>
              <H3 sx={sxStyleH3} color="#595959">
                +91
              </H3>
              <TextField
                id="phonenumber"
                autoFocus
                variant="standard"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",

                  maxLength: maxPhoneLength,
                  style: {
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: "500",
                    color: "#595959",
                    fontSize: 18,
                  },
                }}
                onChange={(e) => {
                  setShowError(false);
                  setPhoneNumber(e.target.value);
                }}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={sxStyleTextFiled}
              />
            </Box>
            {showError && (
              <Small fontSize={12} sx={errorStyle}>
                {errorMessage}
              </Small>
            )}
            <Button
              type="submit"
              variant="contained"
              onClick={() => handleSubmit()}
              size="medium"
              sx={[sxStyleButton, { textTransform: "none" }]}
            >
              Submit
            </Button>
            <Grid sx={sxStyleGrid2}>
              <Small sx={sxStyleSmall2}>
                By validating you are accepting our<br></br>
                <Small
                  sx={{
                    textAlign: "left",
                  }}
                >
                  <Link
                    href={process.env.REACT_APP_TermsAndConditions}
                    target="_blank"
                    color="#008DF9"
                    underline="none"
                  >
                    {" "}
                    Terms and conditions
                  </Link>{" "}
                  &{" "}
                  <Link
                    href={process.env.REACT_APP_PrivacyAndPolicy}
                    target="_blank"
                    color="#008DF9"
                    underline="none"
                  >
                    Privacy policy
                  </Link>
                </Small>
              </Small>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}

const sxStyleGrid = {
  backgroundRepeat: "no-repeat",
  background: process.env.REACT_APP_BUTTON_GRADIENT_COLOR,
  backgroundSize: "cover",
  backgroundPosition: "center",
  maxWidth: {
    xs: "0%",
    sm: "0%",
    md: "80%",
  },
  height: "100vh",
};

const errorStyle = {
  color: "#f00",
  textAlign: "left",
  width: "100%",
  fontFamily: "'Poppins', sans-serif",
  mt: 1,
};
const sxStyleGrid1 = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  height: "100vh",
};

const sxStyleBox = {
  maxWidth: {
    xs: "100%",
    sm: "100%",
    md: "95%",
  },
  my: 0,
  mx: {
    xs: 1,
    sm: 1,
    md: -4,
  },
  background: "#fff",
  borderRadius: 5,
  paddingX: { xs: 1, md: 4, sm: 4 },
  paddingY: 5,
  boxShadow: {
    xs: 0,
    sm: 2,
    md: 3,
  },
};

const sxStyleBoxCaresoul = {
  maxWidth: {
    xs: "0%",
    sm: "0%",
    md: "90%",
  },
  my: 8,
  mx: {
    xs: 0,
    sm: 0,
    md: 4,
  },
  background: "transaparent",
  paddingX: 4,
  paddingY: 0,
};

const sxStyleBoxCaresoulPartner = {
  maxWidth: {
    xs: "0%",
    sm: "0%",
    md: "80%",
  },
  my: 4,
  mx: {
    xs: 0,
    sm: 0,
    md: 4,
  },
  background: "transaparent",
  paddingX: 4,
  paddingY: 5,
};
const sxStyleImage = {
  width: {
    xs: "30%",
    sm: "60%",
    md: "20%",
  },
  mb: 4,
  alignSelf: "center",
};
const sxStyleImage1 = {
  width: {
    xs: "100%",
    sm: "60%",
    md: "20%",
    mt: { xs: 1, sm: 0, md: 0 },
  },
  display: {
    sm: "none",
    md: "none",
    xs: "flex",
  },
  marginBottom: 3,
};
const sxStyleImageCaresoul = {
  width: {
    xs: "100%",
    sm: "100%",
    md: "70%",
  },
  marginBottom: 4,
};

const sxStyleH1 = {
  mt: { md: 3, sm: 3, xs: 3 },
  textAlign: "left",
  fontFamily: "'Poppins', sans-serif",
};
const sxStyleSmall = {
  textAlign: "left",
  width: "100%",
  fontFamily: "'Poppins', sans-serif",
};
const sxStyleBox1 = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  display: "flex",
  mt: 2,
};
const sxStyleBox2 = {
  width: "100%",
  backgroundColor: "#fff",
  border: "0.5px solid #E8E8E8",
  flexDirection: "row",
  display: "flex",
  alignItems: "center",
  borderRadius: 1.2,
  padding: 1,
};
const sxStyleH3 = {
  textAlign: "left",
  fontFamily: "'Poppins', sans-serif",
  fontWeight: "500",
};
const sxStyleTextFiled = {
  ml: 1,
  textAlign: "left",
  fontFamily: "'Poppins', sans-serif",
  fontWeight: "500",
  width: "100%",
  fontSize: "18px",
  color: "#595959",
};
const sxStyleButton = {
  mt: 3,
  mb: 2,
  py: 1,
  fontFamily: "'Poppins', sans-serif",
  width: {
    xs: "100%",
    sm: "100%",
    md: "60%",
  },
  alignSelf: "center",
  background: process.env.REACT_APP_BUTTON_GRADIENT_COLOR,
};
const sxStyleGrid2 = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
};
const sxStyleGridCaresoul = {
  display: { md: "flex", xs: "none", sm: "flex" },
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
};

const sxStyleSmall2 = {
  textAlign: "center",
  maxWidth: {
    xs: "100%",
    sm: "100%",
    md: "100%",
  },
  mb: { xs: "0%", sm: "70%", md: '"70%' },
  fontFamily: "'Poppins', sans-serif",
};
